/**
 * Mock first-round interview content, one pack per function.
 * Generalized from labs-interview-check/hifi.html (the CO object).
 * `{company}` is replaced at render time with the guide page's company name.
 * Questions stay correct for any firm in the function.
 */

export type PackKey = 'consulting' | 'banking' | 'marketing' | 'data';

export interface FollowUp {
  bad: string;
  correct: number;
  good: string;
  options: string[];
  prompt: string;
  why: string;
}

export interface SkillQuestion {
  bad: string;
  correct: number;
  fu: FollowUp;
  good: string;
  hint: string;
  noteFu: string;
  noteGood: string;
  noteHint: string;
  noteMiss: string;
  options: string[];
  prompt: string;
  skill: string;
  why: string;
}

export interface StoryChoice {
  gap?: boolean;
  say: string;
  /** Ends the story builder (the student has no example yet). */
  stop?: boolean;
  t: string;
  w?: number;
}

export interface ExtraQuestion {
  model: string;
  q: string;
}

export interface InterviewPack {
  dims: [string, string, string];
  ext: {
    title: string;
    org: string;
    mark: string;
    color: string;
    steps: [string, string, string];
    line: string;
    externshipId: number;
  };
  /** Exactly 7: the "7 more questions" unlocked by email. */
  extra: ExtraQuestion[];
  key: PackKey;
  qs: [SkillQuestion, SkillQuestion];
  story: {
    prompt: string;
    actions: [StoryChoice, StoryChoice];
    results: [StoryChoice, StoryChoice, StoryChoice];
  };
  track: string;
}

export const SITUATIONS: StoryChoice[] = [
  { t: 'At a part-time job', say: 'At my part-time job', w: 2 },
  { t: 'In a class group project', say: 'In a class group project', w: 1 },
  { t: 'In a student club', say: 'In a student club I’m part of', w: 1 },
  { t: 'Honestly, I can’t think of one', say: '', gap: true, stop: true },
];

export const PACKS: Record<PackKey, InterviewPack> = {
  consulting: {
    key: 'consulting',
    track: 'Consulting',
    dims: ['Structure', 'Numbers', 'Story evidence'],
    qs: [
      {
        skill: 'Structure',
        prompt: 'Say you’re on a {company} team. A retail client’s profits fell 20% last year. Where do you start?',
        options: [
          'Cut costs across the board',
          'Look at what competitors are doing',
          'Split profit into revenue and costs, then find which moved',
          'Spend more on marketing',
        ],
        correct: 2,
        hint: 'What is profit made of?',
        good: 'Good. That’s exactly how I’d want you to open.',
        bad: 'I’d push back. You jumped to a fix before you knew what broke.',
        why: 'Structure before solutions. Profit is revenue minus costs, and interviewers score whether you break the problem down before guessing a fix.',
        fu: {
          prompt: 'Say revenue held flat but costs jumped. What next?',
          options: [
            'Raise prices to cover it',
            'Find which costs rose, and whether it’s one-off or ongoing',
            'Cut headcount',
          ],
          correct: 1,
          good: 'Exactly. One level deeper before recommending anything.',
          bad: 'That’s a fix again. Find the driver first.',
          why: 'Go one level deeper each time: costs, then which costs, then why.',
        },
        noteGood: 'Structured from the first sentence. Went deeper when pushed.',
        noteFu: 'Strong open. Lost the thread on the follow-up.',
        noteHint: 'Needed a nudge to structure, then got there.',
        noteMiss: 'Jumped to fixes before diagnosing.',
      },
      {
        skill: 'Numbers',
        prompt: 'A coffee chain has 200 stores, each selling 400 cups a day at $5. Rough annual revenue?',
        options: ['About $15M', 'About $400M', 'About $1.5B', 'About $146M'],
        correct: 3,
        hint: 'Start with total cups per day across all stores.',
        good: 'Nice, and quick.',
        bad: 'Not quite. Let’s slow down.',
        why: '200 × 400 = 80,000 cups. × $5 = $400K a day. × 365 ≈ $146M. Say the steps out loud; they matter more than the exact number.',
        fu: {
          prompt: 'Now 10% of stores close. Roughly what’s revenue?',
          options: ['Still about $146M', 'About $100M', 'About $131M'],
          correct: 2,
          good: 'Good. You adjusted instead of starting over.',
          bad: 'Adjust the number you already have.',
          why: '$146M × 0.9 ≈ $131M. Build on your last answer.',
        },
        noteGood: 'Comfortable with the math and adjusted on the fly.',
        noteFu: 'Got the estimate. Stumbled on the adjustment.',
        noteHint: 'Got the estimate with a hint.',
        noteMiss: 'Math under pressure needs reps.',
      },
    ],
    story: {
      prompt: 'Tell me about a time you used data to change someone’s mind.',
      actions: [
        {
          t: 'We looked at the numbers together and talked it through',
          say: 'we looked at the numbers together and talked it through',
          w: 0,
        },
        {
          t: 'I pulled the numbers, found the pattern, and made one chart to show it',
          say: 'I pulled the numbers myself, found the pattern, and made one chart to show it',
          w: 1,
        },
      ],
      results: [
        {
          t: 'They changed the plan, and I can say what improved',
          say: 'They changed the plan, and I can point to what improved.',
          w: 1,
        },
        { t: 'People agreed it was a good point', say: 'People agreed it was a good point.', w: 0 },
        { t: 'Honestly, I’m not sure what changed', say: 'I’m honestly not sure what changed after.', gap: true },
      ],
    },
    ext: {
      title: 'Amazon Operational Strategy & People Analytics',
      org: 'Amazon',
      mark: 'a',
      color: '#ff9900',
      steps: [
        'Frame fulfillment-center attrition as a business problem, like a consultant',
        'Analyze real employee-voice data in Python',
        'Find the root cause and recommend a pilot to stakeholders',
      ],
      line: 'I looked at attrition in Amazon fulfillment centers, framed it as a business risk, found the root cause in employee reviews, and recommended a pilot for the top issue.',
      externshipId: 663,
    },
    extra: [
      {
        q: 'Why consulting, and why {company}?',
        model:
          'Tie it to something you have actually done: “I like breaking a messy problem into pieces and I found that out running X.” Then name one or two specifics about the firm you learned from its people or its work, not its homepage. Close with what you want to learn in the first year.',
      },
      {
        q: 'Estimate the number of gas stations in the US.',
        model:
          'Start from demand. The US has roughly 280M registered vehicles; assume each fills up about once a week, or about 280M fill-ups a week. If a station handles around 300 fill-ups a day, that’s about 2,000 a week. 280M ÷ 2,000 ≈ 140,000 stations. State each assumption out loud and sanity-check the result at the end.',
      },
      {
        q: 'A client wants to enter a new market. How would you structure it?',
        model:
          'Four buckets: is the market attractive (size, growth, margins), who else is there (competitors and how they’d react), can the client win (capabilities, brand, cost position), and how to enter (build, buy, or partner). Finish with the economics: investment, payback, and the risks that would change the answer.',
      },
      {
        q: 'A product sells for $20. Variable cost is $12 per unit and fixed costs are $400K a year. How many units to break even?',
        model:
          'Each unit contributes $20 − $12 = $8 toward fixed costs. $400,000 ÷ $8 = 50,000 units a year. Then ask whether 50,000 is realistic given the market size.',
      },
      {
        q: 'Tell me about a time you worked on a team that disagreed.',
        model:
          'Situation: a group project split on direction a week before the deadline. Task: I needed us to pick one plan. Action: I laid both options side by side against the grading criteria and asked each person which risks they could live with. Result: we picked one in a 20-minute call and finished on time. Name the real numbers from your version.',
      },
      {
        q: 'Tell me about a time you led without formal authority.',
        model:
          'Situation: a club event with no clear owner was slipping. Task: get it back on track without being the president. Action: I made a one-page task list, asked each person to own one line, and ran a 10-minute weekly check-in. Result: the event happened on schedule and turnout beat the prior year. Say by how much.',
      },
      {
        q: 'How would you present a recommendation to a skeptical client?',
        model:
          'Lead with the answer, then the two or three reasons behind it, then the evidence for each. Name the objection before they do and show the data that addresses it. End with the one decision you need from them and the next step.',
      },
    ],
  },

  banking: {
    key: 'banking',
    track: 'Banking & finance',
    dims: ['Accounting', 'Valuation', 'Story evidence'],
    qs: [
      {
        skill: 'Accounting',
        prompt: 'Depreciation goes up by $10. Tax rate is 25%. What happens to net income?',
        options: ['Falls by $7.50', 'Falls by $10', 'Rises by $2.50', 'No change, it’s non-cash'],
        correct: 0,
        hint: 'Taxes go down too.',
        good: 'Right.',
        bad: 'Careful.',
        why: 'Pre-tax income drops $10 and taxes drop $2.50. Net income falls $7.50.',
        fu: {
          prompt: 'And what happens to cash?',
          options: ['Falls by $7.50', 'No change', 'Rises by $2.50'],
          correct: 2,
          good: 'That’s the full walk.',
          bad: 'Depreciation is non-cash. Add it back.',
          why: 'Then add back $10 of non-cash depreciation and cash is up $2.50.',
        },
        noteGood: 'Walked the statements cleanly, including cash.',
        noteFu: 'Got net income. Missed the cash add-back.',
        noteHint: 'Forgot taxes at first, recovered.',
        noteMiss: 'Three-statement links need work.',
      },
      {
        skill: 'Valuation',
        prompt: 'Which method usually gives the highest valuation?',
        options: ['Trading comparables', 'Book value', 'Precedent transactions', 'The 52-week range'],
        correct: 2,
        hint: 'Think about what a buyer pays that a stock trader doesn’t.',
        good: 'Good.',
        bad: 'Hm, not quite.',
        why: 'Precedents include a control premium, the extra a buyer pays to take over the whole company.',
        fu: {
          prompt: 'Why do precedents come out higher?',
          options: ['They use older data', 'Buyers pay a control premium', 'They ignore debt'],
          correct: 1,
          good: 'That’s the one-sentence answer I want.',
          bad: 'It’s about paying for control.',
          why: 'A buyer pays extra for control. That premium is baked into precedent prices.',
        },
        noteGood: 'Knew the method and the reason behind it.',
        noteFu: 'Knew the answer, not the why.',
        noteHint: 'Got there with a nudge.',
        noteMiss: 'Valuation intuition needs reps.',
      },
    ],
    story: {
      prompt: 'Tell me about a time you worked with numbers under a deadline.',
      actions: [
        { t: 'We split up the work and got it done', say: 'we split up the work and got it done', w: 0 },
        {
          t: 'I built the spreadsheet myself and checked every formula',
          say: 'I built the spreadsheet myself and checked every formula before it went out',
          w: 1,
        },
      ],
      results: [
        {
          t: 'It was used for a real decision, and I can say which',
          say: 'It was used to make a real decision, and I can tell you which.',
          w: 1,
        },
        { t: 'It turned out fine', say: 'It turned out fine.', w: 0 },
        {
          t: 'Honestly, I’m not sure what happened with it',
          say: 'I’m honestly not sure what happened with it after.',
          gap: true,
        },
      ],
    },
    ext: {
      title: 'Attronica FP&A and Private Company Valuation',
      org: 'Attronica',
      mark: 'At',
      color: '#0b6e4f',
      steps: [
        'Diagnose a real company’s financial health from its statements',
        'Build a five-year projection for its AV division and defend it',
        'Value the division with a DCF, cross-check with comps, pitch a buyer',
      ],
      line: 'I built a five-year projection for a real company’s AV division, valued it with a DCF, and cross-checked it against comparables. I can walk you through where they disagreed.',
      externshipId: 677,
    },
    extra: [
      {
        q: 'Walk me through the three financial statements.',
        model:
          'The income statement shows revenue, expenses, and net income over a period. The balance sheet shows assets, liabilities, and equity at a point in time, and assets always equal liabilities plus equity. The cash flow statement starts with net income, adjusts for non-cash items and working capital, then adds investing and financing flows to get the change in cash. Net income flows into retained earnings, and ending cash flows onto the balance sheet.',
      },
      {
        q: 'Walk me through a DCF.',
        model:
          'Project unlevered free cash flow for about five years. Estimate a terminal value, either with a perpetuity growth rate or an exit multiple. Discount the cash flows and terminal value back at the WACC to get enterprise value. Then subtract net debt to get equity value, and divide by diluted shares for a per-share value.',
      },
      {
        q: 'What’s the difference between enterprise value and equity value?',
        model:
          'Equity value is what the shareholders own. Enterprise value is the value of the whole business to all capital providers: equity value plus debt, preferred stock, and minority interest, minus cash. Use EV with metrics like EBITDA that belong to everyone, and equity value with metrics like net income that belong only to shareholders.',
      },
      {
        q: 'A company earns $5 per share and trades at a P/E of 20. What’s the share price?',
        model:
          'P/E is price divided by earnings per share. $5 × 20 = $100 a share. Then say what could justify a 20x multiple, like growth well above peers.',
      },
      {
        q: 'Interest rates rise. What happens to existing bond prices, and why?',
        model:
          'They fall. New bonds now pay a higher coupon, and an older bond with a lower coupon is only attractive at a lower price. Longer-maturity bonds fall more because their cash flows are discounted over more years.',
      },
      {
        q: 'Why finance, and why {company}?',
        model:
          'Start with a real moment that pulled you in, like a class, a stock you followed, or a spreadsheet you built for a job. Connect it to the specific group or business you’re applying to and what you’d learn there. Mention something concrete you learned from talking to someone at the firm.',
      },
      {
        q: 'Tell me about a time you made a mistake.',
        model:
          'Situation: I sent a budget sheet with a wrong formula to my club’s treasurer. Task: fix it before it drove a decision. Action: I flagged it the same day, corrected it, and added a check row that totals every column. Result: the budget passed with the right numbers and I have used a check row since. Pick a real mistake with a real fix, never a disguised strength.',
      },
    ],
  },

  marketing: {
    key: 'marketing',
    track: 'Brand marketing',
    dims: ['Marketing case', 'Reading numbers', 'Story evidence'],
    qs: [
      {
        skill: 'Marketing case',
        prompt: '{company} wants Gen Z customers to try a new product line. What’s your first move?',
        options: [
          'Launch a TikTok campaign now',
          'Cut the price 20%',
          'Sign a famous creator or athlete',
          'Figure out who the customer is and what stops them trying it',
        ],
        correct: 3,
        hint: 'Before choosing a channel, who are you talking to?',
        good: 'Love that. Most people jump straight to the channel.',
        bad: 'That’s a tactic, not a first move.',
        why: 'Who and why before where. Find the audience and the insight before picking a channel.',
        fu: {
          prompt: 'What’s the one thing you’d want to learn about those customers?',
          options: [
            'Their favorite social platform',
            'What stops them trying it today',
            'How much they spend in the category',
          ],
          correct: 1,
          good: 'Yes. The barrier is the insight.',
          bad: 'Useful later, but not the insight.',
          why: 'The barrier becomes the message. The channel comes after.',
        },
        noteGood: 'Started with the audience. Found the insight.',
        noteFu: 'Right instinct, fuzzy on the insight.',
        noteHint: 'Went to tactics first, then corrected.',
        noteMiss: 'Leads with tactics over audience.',
      },
      {
        skill: 'Reading numbers',
        prompt: 'Campaign A: 2M views, 1,000 purchases. B: 200K views, 800 purchases. Which converted better?',
        options: ['B, about 8x the conversion rate', 'A, 10x the reach', 'About the same', 'Can’t compare them'],
        correct: 0,
        hint: 'Divide purchases by views for each.',
        good: 'Exactly. Reach is the vanity number.',
        bad: 'Check the rate, not the total.',
        why: '1,000 ÷ 2M = 0.05%. 800 ÷ 200K = 0.4%. Rates beat raw totals.',
        fu: {
          prompt: 'Before calling B the winner, what else would you check?',
          options: ['Which got more likes', 'Which ran longer', 'Cost per purchase for each'],
          correct: 2,
          good: 'Exactly. That decides where the next dollar goes.',
          bad: 'That’s another vanity number.',
          why: 'Cost per purchase decides what to fund next.',
        },
        noteGood: 'Looked past reach to conversion and cost.',
        noteFu: 'Read the rate, missed cost.',
        noteHint: 'Distracted by reach at first.',
        noteMiss: 'Reads totals, not rates.',
      },
    ],
    story: {
      prompt: 'Tell me about a time you got people to care about something.',
      actions: [
        { t: 'We posted about it a lot', say: 'we posted about it a lot', w: 0 },
        {
          t: 'I figured out who we were trying to reach and rewrote the message for them',
          say: 'I figured out who we were trying to reach and rewrote the message for them',
          w: 1,
        },
      ],
      results: [
        { t: 'Turnout went up, and I know by how much', say: 'Turnout went up, and I know by how much.', w: 1 },
        { t: 'People seemed to like it', say: 'People seemed to like it.', w: 0 },
        { t: 'Honestly, I’m not sure it worked', say: 'I’m honestly not sure it worked.', gap: true },
      ],
    },
    ext: {
      title: 'Beats by Dre Creative Advertising Strategy',
      org: 'Beats by Dre',
      mark: 'b',
      color: '#e01f26',
      steps: [
        'Map the video ad landscape and build an ad taxonomy',
        'Run a consumer ad review: survey plus social listening',
        'Build Beats’ creative strategy framework and present it',
      ],
      line: 'I ran a consumer review of video ads for Beats by Dre, built a creative effectiveness rubric from it, and presented a strategy for their next campaign.',
      externshipId: 671,
    },
    extra: [
      {
        q: 'Pick a brand you think markets well. Why does it work?',
        model:
          'Name the brand, then its target customer in one sentence and the one idea it owns in their head. Give one campaign or touchpoint as proof, and say what you would change. Specific beats famous: a small brand you know well is a stronger answer than a generic take on a giant.',
      },
      {
        q: 'How would you know if a campaign worked?',
        model:
          'Start from the goal set before launch: awareness, consideration, or sales. Pick the metric that matches, like reach and recall for awareness or conversion and cost per purchase for sales. Compare against a baseline or a holdout group that didn’t see the campaign, not just the before and after.',
      },
      {
        q: 'Walk me through the 4 Ps for a product you like.',
        model:
          'Product: what it is and the need it meets. Price: where it sits against alternatives and what that signals. Place: where customers can buy it. Promotion: how the brand tells people about it. Finish by naming which P you would change first and why.',
      },
      {
        q: 'An email goes to 50,000 people. 20% open it and 5% of openers click. How many clicks?',
        model:
          '50,000 × 20% = 10,000 opens. 10,000 × 5% = 500 clicks. That is a 1% click rate on the full list, which is the number to compare against other sends.',
      },
      {
        q: 'We spent $30,000 on ads and got 600 new customers. Is that good?',
        model:
          '$30,000 ÷ 600 = $50 per customer. Whether that’s good depends on what a customer is worth: if the average customer brings in well over $50 in profit over time, it’s working. I would also compare it to the cost per customer from other channels.',
      },
      {
        q: 'Tell me about a time your idea got pushback.',
        model:
          'Situation: I pitched a new format for our club’s recruiting posts and the board preferred the old one. Task: get a fair test. Action: I proposed running both for two weeks and tracking sign-ups from each. Result: the new format drove more sign-ups and became the default. Use your real numbers and be generous about the other side.',
      },
      {
        q: 'Why marketing, and why {company}?',
        model:
          'Name the moment you realized you like figuring out why people buy, ideally something you ran yourself. Then show you know the brand: who its customer is and one thing it does that you admire or would challenge. Close with what you would want to work on in the role.',
      },
    ],
  },

  data: {
    key: 'data',
    track: 'Data & analytics',
    dims: ['Diagnosis', 'Reading numbers', 'Story evidence'],
    qs: [
      {
        skill: 'Diagnosis',
        prompt: 'Say you’re on a {company} team. Daily active users fell 10% overnight. What’s your first move?',
        options: [
          'Tell the team to roll back the last release',
          'Launch a re-engagement email campaign',
          'Check it’s not a tracking bug, then segment by platform, region, and version',
          'Wait a week to see if it recovers',
        ],
        correct: 2,
        hint: 'Is the drop real? And where is it?',
        good: 'Good. Confirm it’s real, then find where it lives.',
        bad: 'That’s a fix. You don’t know what broke yet.',
        why: 'A sudden overnight drop is often a logging or pipeline issue. Rule that out, then segment to see where the drop is concentrated.',
        fu: {
          prompt: 'Tracking checks out. The drop is almost all on Android. What next?',
          options: [
            'Assume it’s seasonality',
            'Survey all users about the app',
            'Check what changed on Android that day, like a new app version',
          ],
          correct: 2,
          good: 'Exactly. Tie the drop to a change.',
          bad: 'Seasonality wouldn’t hit only one platform.',
          why: 'A drop on one platform points to something platform-specific. Look for a release, an OS update, or an app store issue on that date.',
        },
        noteGood: 'Checked data quality first, then segmented cleanly.',
        noteFu: 'Good first check. Didn’t link the drop to a change.',
        noteHint: 'Needed a nudge to question the data.',
        noteMiss: 'Jumped to fixes before confirming the drop.',
      },
      {
        skill: 'Reading numbers',
        prompt:
          'Checkout test. Control: 400 purchases from 10,000 visitors. New design: 460 from 10,000. What’s the lift?',
        options: ['6% relative lift', '15% relative lift', '60% relative lift', 'Can’t tell without revenue'],
        correct: 1,
        hint: 'Get each conversion rate first.',
        good: 'Right. Rates first, then the change.',
        bad: 'Close. Compute both rates before comparing.',
        why: '400 ÷ 10,000 = 4.0%. 460 ÷ 10,000 = 4.6%. That’s 0.6 points, and 0.6 ÷ 4.0 = a 15% relative lift. Say which one you mean.',
        fu: {
          prompt: 'Before you ship it, what would you check?',
          options: [
            'Whether the difference is statistically significant',
            'Which version the team likes better',
            'Whether the new design looks more modern',
          ],
          correct: 0,
          good: 'Yes. A lift you can’t trust isn’t a lift.',
          bad: 'Opinions don’t settle a test.',
          why: 'Check significance and that the test ran long enough. Here z is about 2.1, which clears the usual 5% bar, but only just.',
        },
        noteGood: 'Got the rates, the lift, and asked about significance.',
        noteFu: 'Got the lift. Didn’t question whether it’s real.',
        noteHint: 'Needed a nudge to compare rates.',
        noteMiss: 'Mixes up absolute and relative change.',
      },
    ],
    story: {
      prompt: 'Tell me about a time you found something surprising in data.',
      actions: [
        {
          t: 'We looked through the spreadsheet as a group',
          say: 'we looked through the spreadsheet as a group',
          w: 0,
        },
        {
          t: 'I cleaned the data myself and checked the pattern held before sharing it',
          say: 'I cleaned the data myself and checked the pattern held before I shared it',
          w: 1,
        },
      ],
      results: [
        {
          t: 'Someone acted on it, and I can say what changed',
          say: 'Someone acted on it, and I can tell you what changed.',
          w: 1,
        },
        { t: 'People thought it was interesting', say: 'People thought it was interesting.', w: 0 },
        {
          t: 'Honestly, I’m not sure anything changed',
          say: 'I’m honestly not sure anything changed after.',
          gap: true,
        },
      ],
    },
    ext: {
      title: 'Breaking Games E-Commerce Data Analysis & Strategy',
      org: 'Breaking Games',
      mark: 'BG',
      color: '#5b2a86',
      steps: [
        'Dig into a real e-commerce company’s sales data',
        'Find what drives purchases and where customers drop off',
        'Turn the analysis into a strategy recommendation',
      ],
      line: 'I analyzed real sales data for Breaking Games, found what drives purchases and where customers drop off, and turned it into a strategy recommendation.',
      externshipId: 668,
    },
    extra: [
      {
        q: 'Write a query for the top 5 products by revenue last month.',
        model:
          'SELECT product_id, SUM(price * quantity) AS revenue FROM orders WHERE order_date >= [first day of last month] AND order_date < [first day of this month] GROUP BY product_id ORDER BY revenue DESC LIMIT 5. Say out loud how you would handle refunds or cancelled orders.',
      },
      {
        q: 'What’s the difference between an INNER JOIN and a LEFT JOIN?',
        model:
          'An INNER JOIN keeps only rows that match in both tables. A LEFT JOIN keeps every row from the left table and fills in NULLs where the right table has no match. Use a LEFT JOIN when you need to keep, for example, users who never placed an order.',
      },
      {
        q: 'When would you report the median instead of the mean?',
        model:
          'When the data is skewed or has outliers, like order values where a few huge orders pull the mean up. The median shows what a typical customer does. Report both if the gap between them is itself the story.',
      },
      {
        q: 'Users who use feature X retain better. Should we push everyone to use it?',
        model:
          'Not yet. That’s a correlation, and engaged users may simply be more likely to find the feature. To test cause, run an experiment that nudges a random group toward the feature and compare retention against a control group.',
      },
      {
        q: 'A page gets 1,200 visitors and 36 purchases. What’s the conversion rate, and what would it need to be for 60 purchases?',
        model:
          '36 ÷ 1,200 = 3%. For 60 purchases from the same traffic you need 60 ÷ 1,200 = 5%. Then ask whether more traffic at 3% would be the cheaper route.',
      },
      {
        q: 'How would you measure whether a new feature is a success?',
        model:
          'Start with the goal the feature serves and pick one primary metric for it, like conversion or weekly retention. Add guardrail metrics that must not get worse, like load time or refunds. Decide the success threshold before launch and compare against a control group if you can.',
      },
      {
        q: 'Tell me about a time you explained a technical finding to a non-technical person.',
        model:
          'Situation: my manager at a part-time job wanted to add evening hours. Task: show whether demand was there. Action: I pulled a few months of sales by hour and made one chart with a one-line takeaway, no jargon. Result: they tried a shorter evening window instead and kept it. Lead with the decision, not the method.',
      },
    ],
  },
};
